/**
 * Admin API Service
 *
 * Provides API functions for super admin operations including
 * BoldSign sender identity management for client organizations.
 */

import { api } from './api';

// ============================================
// TYPES
// ============================================

export type BoldSignStatus = 'not_configured' | 'pending_approval' | 'approved' | 'revoked';

export interface BoldSignStatusResponse {
  status: BoldSignStatus;
  message?: string;
  email?: string;
  senderIdentityId?: string;
  approvedAt?: string;
  lastVerifiedAt?: string;
  createdAt?: string;
  contractsEnabled?: boolean;
  contractsEnabledAt?: string;
}

export interface ToggleContractAccessResponse {
  message: string;
  contractsEnabled: boolean;
  contractsEnabledAt?: string;
  status: BoldSignStatus;
}

export interface EnableContractsParams {
  email?: string;
  businessName?: string;
}

export interface EnableContractsResponse {
  message: string;
  senderIdentityId: string;
  email: string;
  status: BoldSignStatus;
}

// Blocked Email Domains Types
export interface BlockedEmailDomain {
  _id: string;
  domain: string;
  reason?: string;
  addedBy: {
    _id: string;
    email: string;
    profile?: {
      firstName?: string;
      lastName?: string;
    };
  };
  isBuiltIn: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BlockedDomainsListResponse {
  domains: BlockedEmailDomain[];
  total: number;
}

export interface AddBlockedDomainParams {
  domain: string;
  reason?: string;
}

// ============================================
// ADMIN BOLDSIGN API
// ============================================

export const adminBoldsignApi = {
  /**
   * Enable contracts for an organization
   * Creates a BoldSign sender identity for the organization
   */
  enableContracts: async (
    orgId: string,
    params?: EnableContractsParams
  ): Promise<EnableContractsResponse> => {
    const response = await api.post(`/admin/organizations/${orgId}/boldsign/enable`, params || {});
    return response.data.data;
  },

  /**
   * Check BoldSign status for an organization
   * Returns current status and syncs with BoldSign API
   */
  checkStatus: async (orgId: string): Promise<BoldSignStatusResponse> => {
    const response = await api.get(`/admin/organizations/${orgId}/boldsign/status`);
    return response.data.data;
  },

  /**
   * Resend approval email to the organization owner
   * Only works when status is 'pending_approval'
   */
  resendApproval: async (orgId: string): Promise<{ message: string }> => {
    const response = await api.post(`/admin/organizations/${orgId}/boldsign/resend`);
    return response.data.data;
  },

  /**
   * Revoke/disable contracts access for an organization
   * Revokes the BoldSign sender identity
   */
  revokeAccess: async (orgId: string): Promise<{ message: string }> => {
    const response = await api.post(`/admin/organizations/${orgId}/boldsign/revoke`);
    return response.data.data;
  },

  /**
   * Toggle contract access for an organization
   * This controls whether the organization can actually use contracts,
   * independent of BoldSign approval status
   */
  toggleContractAccess: async (orgId: string, enabled: boolean): Promise<ToggleContractAccessResponse> => {
    const response = await api.post(`/admin/organizations/${orgId}/boldsign/toggle-access`, { enabled });
    return response.data.data;
  },
};

// ============================================
// ADMIN BLOCKED DOMAINS API
// ============================================

export const adminBlockedDomainsApi = {
  /**
   * List all blocked email domains
   */
  list: async (): Promise<BlockedDomainsListResponse> => {
    const response = await api.get('/admin/blocked-domains');
    return response.data.data;
  },

  /**
   * Add a new blocked domain
   */
  add: async (params: AddBlockedDomainParams): Promise<{ domain: BlockedEmailDomain; message: string }> => {
    const response = await api.post('/admin/blocked-domains', params);
    return response.data.data;
  },

  /**
   * Remove a blocked domain
   */
  remove: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/blocked-domains/${id}`);
    return response.data.data;
  },

  /**
   * Seed the database with built-in blocked domains
   */
  seed: async (): Promise<{ message: string; added: number; skipped: number }> => {
    const response = await api.post('/admin/blocked-domains/seed');
    return response.data.data;
  },
};

// ============================================
// SERVICE TYPES TYPES
// ============================================

export interface ServiceType {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceTypesListResponse {
  serviceTypes: ServiceType[];
  total: number;
}

export interface CreateServiceTypeParams {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
  order?: number;
}

export interface UpdateServiceTypeParams {
  name?: string;
  slug?: string;
  description?: string;
  icon?: string;
  isActive?: boolean;
  order?: number;
}

// ============================================
// ADMIN SERVICE TYPES API
// ============================================

export const adminServiceTypesApi = {
  /**
   * List all service types (includes inactive)
   */
  list: async (): Promise<ServiceTypesListResponse> => {
    const response = await api.get('/admin/service-types');
    return response.data.data;
  },

  /**
   * Create a new service type
   */
  create: async (params: CreateServiceTypeParams): Promise<{ serviceType: ServiceType; message: string }> => {
    const response = await api.post('/admin/service-types', params);
    return response.data.data;
  },

  /**
   * Update a service type
   */
  update: async (id: string, params: UpdateServiceTypeParams): Promise<{ serviceType: ServiceType; message: string }> => {
    const response = await api.put(`/admin/service-types/${id}`, params);
    return response.data.data;
  },

  /**
   * Delete (deactivate) a service type
   */
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/service-types/${id}`);
    return response.data.data;
  },

  /**
   * Seed the database with initial service types
   */
  seed: async (): Promise<{ message: string; added: number; skipped: number }> => {
    const response = await api.post('/admin/service-types/seed');
    return response.data.data;
  },

  /**
   * Reorder service types
   */
  reorder: async (ids: string[]): Promise<{ message: string }> => {
    const response = await api.post('/admin/service-types/reorder', { ids });
    return response.data.data;
  },
};

// ============================================
// SUBSCRIPTION OVERRIDE TYPES
// ============================================

export type SubscriptionTier = 'trial' | 'launch' | 'rise' | 'summit' | 'expired';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'cancelled' | 'expired';

export interface SubscriptionOverrideParams {
  tier?: SubscriptionTier;
  status?: SubscriptionStatus;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  notes?: string;
}

export interface OrganizationSubscription {
  tier?: SubscriptionTier;
  status?: SubscriptionStatus;
  currentPeriodEnd?: string;
  trialEndsAt?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  adminOverride?: {
    overriddenAt: string;
    overriddenBy: string;
    notes: string;
  };
}

export interface AdminOrganization {
  _id: string;
  name: string;
  slug: string;
  email?: string;
  subscription?: OrganizationSubscription;
  createdAt: string;
  updatedAt: string;
}

// ============================================
// ADMIN SUBSCRIPTION API
// ============================================

export const adminSubscriptionApi = {
  /**
   * Override an organization's subscription
   * Allows super admins to manually set tier, status, and expiration dates
   */
  override: async (
    orgId: string,
    params: SubscriptionOverrideParams
  ): Promise<{ organization: AdminOrganization; message: string }> => {
    const response = await api.put(`/admin/organizations/${orgId}/subscription`, params);
    return response.data.data;
  },
};
